/**
 * Token, user and the stable device id, kept in secure storage (expo-secure-store on the
 * phone; an in-memory map in tests).
 */
import type { User } from "./types";

export interface SecureKV {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
  deleteItem(key: string): Promise<void>;
}

const K_TOKEN = "npac.token";
const K_USER = "npac.user";
const K_DEVICE = "npac.deviceId";
const K_ASSEMBLY = "npac.assemblyId";

/** Generated once per install and sent with every write. */
export async function getOrCreateDeviceId(kv: SecureKV, makeId: () => string): Promise<string> {
  const existing = await kv.getItem(K_DEVICE);
  if (existing) return existing;
  const id = makeId();
  await kv.setItem(K_DEVICE, id);
  return id;
}

export interface SessionState {
  token: string | null;
  user: User | null;
  assemblyId: string | null;
}

export class Session {
  state: SessionState = { token: null, user: null, assemblyId: null };

  constructor(private kv: SecureKV) {}

  async load(): Promise<SessionState> {
    const [token, userJson, assemblyId] = await Promise.all([this.kv.getItem(K_TOKEN), this.kv.getItem(K_USER), this.kv.getItem(K_ASSEMBLY)]);
    let user: User | null = null;
    try {
      user = userJson ? (JSON.parse(userJson) as User) : null;
    } catch {
      user = null;
    }
    this.state = { token, user, assemblyId: assemblyId ?? user?.assemblyId ?? null };
    return this.state;
  }

  async save(token: string, user: User): Promise<void> {
    await this.kv.setItem(K_TOKEN, token);
    await this.kv.setItem(K_USER, JSON.stringify(user));
    const assemblyId = user.assemblyId ?? (this.state.user?.id === user.id ? this.state.assemblyId : null);
    if (assemblyId) await this.kv.setItem(K_ASSEMBLY, assemblyId);
    else await this.kv.deleteItem(K_ASSEMBLY);
    this.state = { token, user, assemblyId };
  }

  /** The same login, as the server sees it now (duties, booths, name changed by an admin); the open assembly stays. */
  async setUser(user: User): Promise<void> {
    if (!this.state.token || this.state.user?.id !== user.id) return;
    await this.kv.setItem(K_USER, JSON.stringify(user));
    this.state = { ...this.state, user };
  }

  async setAssembly(assemblyId: string): Promise<void> {
    await this.kv.setItem(K_ASSEMBLY, assemblyId);
    this.state = { ...this.state, assemblyId };
  }

  /** 401: forget the token only. User, cached booths, drafts and the outbox stay. */
  async expire(): Promise<void> {
    await this.kv.deleteItem(K_TOKEN);
    this.state = { ...this.state, token: null };
  }

  /** Explicit logout. The outbox is still kept on the phone and sent after the next login. */
  async clear(): Promise<void> {
    await Promise.all([this.kv.deleteItem(K_TOKEN), this.kv.deleteItem(K_USER), this.kv.deleteItem(K_ASSEMBLY)]);
    this.state = { token: null, user: null, assemblyId: null };
  }
}

export class MemoryKV implements SecureKV {
  private m = new Map<string, string>();
  async getItem(k: string) {
    return this.m.has(k) ? this.m.get(k)! : null;
  }
  async setItem(k: string, v: string) {
    this.m.set(k, v);
  }
  async deleteItem(k: string) {
    this.m.delete(k);
  }
}
